import { FC, ReactElement, useContext } from "react";

import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";

import {
  BuilderBottomToolbar,
  BuilderBottomToolbarSeparator,
  BuilderBottomToolbarUndoRedo,
} from "../../shared/BuilderBottomToolbar";

import AddMediaDialog from "@src/features/app/dialogs/addMedia/AddMediaDialog";
import { MediaFragment } from "@src/fragments.generated";
import { LinkType, getLinkType } from "@src/lib/utils/files";

import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import FileOutlineIcon from "@src/lib/icons/18px/file-outline";
import LineDashedIcon from "@src/lib/icons/18px/line-dashed";
import MobileOutlineIcon from "@src/lib/icons/18px/mobile-outline";
import OrderedListIcon from "@src/lib/icons/18px/ordered-list";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import Table3RowsOutlineIcon from "@src/lib/icons/18px/table-3-rows-outline";
import TextSizeIcon from "@src/lib/icons/18px/text-size";
import UnorderedListIcon from "@src/lib/icons/18px/unordered-list";

import RichTextEditorFormattingToolbar from "../../course/RichTextEditorFormattingToolbar";
import { TiptapEditorContext } from "../../course/contexts/TiptapEditorContext";
import useTrainingResourceViewModeQueryParams from "../hooks/useTrainingResourceViewModeQueryParams";

type Props = {
  showEditControls?: boolean;
};

const TrainingResourceRichTextEditorBottomToolbar: FC<Props> = ({
  showEditControls = true,
}) => {
  const tiptapEditorContext = useContext(TiptapEditorContext);
  const { trainingResourceViewMode, setTrainingResourceViewMode } =
    useTrainingResourceViewModeQueryParams();
  const currentViewMode = trainingResourceViewMode || "document-view";

  const handleImageUpload = (media: MediaFragment) => {
    if (!tiptapEditorContext?.editor) return;
    const imageUrl =
      media.unoptimizedUrl || media.imageUrls?.original || media.mediaUrls.en;
    if (!imageUrl) return;
    const linkType = getLinkType(imageUrl);
    if (linkType !== LinkType.IMAGE && linkType !== LinkType.GIF) return;
    tiptapEditorContext.editor
      .chain()
      .focus()
      .setImage({ src: imageUrl })
      .run();
  };

  const handleLinkUpload = (media: MediaFragment) => {
    if (!tiptapEditorContext?.editor) return;
    const linkUrl = media.mediaUrls?.en || media.unoptimizedUrl;
    if (!linkUrl) return;
    tiptapEditorContext.editor
      .chain()
      .focus()
      .setLink({ href: linkUrl, target: "_blank" })
      .insertContent(linkUrl)
      .run();
  };

  const onMediaChange = (media: MediaFragment) => {
    if (!media) return;
    const url =
      media.unoptimizedUrl || media.imageUrls?.original || media.mediaUrls.en;
    const linkType = getLinkType(url);
    if (linkType === LinkType.IMAGE || linkType === LinkType.GIF) {
      handleImageUpload(media);
    } else if (linkType === LinkType.LINK) {
      handleLinkUpload(media);
    }
  };

  const headingOptions: {
    key: string;
    label: string;
    active: boolean;
    onClick: () => void;
    textType?: "H3" | "P1" | "P2" | "P3";
    fontWeight?: "SemiBold" | "Bold";
  }[] = [
    {
      key: "h1",
      label: "Heading 1",
      active: !!tiptapEditorContext?.formatting.isHeading1,
      textType: "H3",
      fontWeight: "Bold",
      onClick: () =>
        tiptapEditorContext?.editor
          ?.chain()
          .focus()
          .toggleHeading({ level: 1 })
          .run(),
    },
    {
      key: "h2",
      label: "Heading 2",
      active: !!tiptapEditorContext?.formatting.isHeading2,
      textType: "P1",
      fontWeight: "SemiBold",
      onClick: () =>
        tiptapEditorContext?.editor
          ?.chain()
          .focus()
          .toggleHeading({ level: 2 })
          .run(),
    },
    {
      key: "h3",
      label: "Heading 3",
      active: !!tiptapEditorContext?.formatting.isHeading3,
      textType: "P2",
      fontWeight: "SemiBold",
      onClick: () =>
        tiptapEditorContext?.editor
          ?.chain()
          .focus()
          .toggleHeading({ level: 3 })
          .run(),
    },
    {
      key: "body",
      label: "Body",
      active: !!tiptapEditorContext?.formatting.isParagraph,
      textType: "P2",
      onClick: () => tiptapEditorContext?.editor?.commands.setParagraph(),
    },
  ];
  const activeHeading =
    headingOptions.find((opt) => opt.active) ??
    headingOptions[headingOptions.length - 1];

  const listOptions: {
    key: string;
    label: string;
    icon: ReactElement;
    active: boolean;
    onClick: () => void;
  }[] = [
    {
      key: "bullet",
      label: "Bulleted list",
      icon: <UnorderedListIcon />,
      active: !!tiptapEditorContext?.formatting.isBulletList,
      onClick: () =>
        tiptapEditorContext?.editor?.chain().focus().toggleBulletList().run(),
    },
    {
      key: "ordered",
      label: "Numbered list",
      icon: <OrderedListIcon />,
      active: !!tiptapEditorContext?.formatting.isOrderedList,
      onClick: () =>
        tiptapEditorContext?.editor?.chain().focus().toggleOrderedList().run(),
    },
  ];
  const activeList = listOptions.find((opt) => opt.active);

  const viewModeButtons: {
    key: "document-view" | "mobile-view";
    label: string;
    icon: ReactElement;
  }[] = [
    {
      key: "document-view",
      label: "Document",
      icon: <FileOutlineIcon />,
    },
    {
      key: "mobile-view",
      label: "Mobile",
      icon: <MobileOutlineIcon />,
    },
  ];

  return (
    <BuilderBottomToolbar>
      {showEditControls && (
        <>
          <BuilderBottomToolbarUndoRedo />
          <BuilderBottomToolbarSeparator />

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-md"
                    className="gap-0.5 px-1.5"
                    aria-label={`Text style (${activeHeading.label})`}
                  >
                    <TextSizeIcon />
                    <ChevronDownIcon className="text-gray-8" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="top">
                Text style ({activeHeading.label})
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              {headingOptions.map((option) => (
                <DropdownMenuItem
                  key={option.key}
                  onClick={option.onClick}
                  className={cn(option.active && "bg-gray-1")}
                >
                  <Text
                    type={option.textType ?? "P2"}
                    fontWeight={option.fontWeight}
                    className="text-gray-12"
                  >
                    {option.label}
                  </Text>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <BuilderBottomToolbarSeparator />

          <div className="flex items-center [&_button]:rounded-full [&>div]:!flex-none [&>div]:!justify-start [&>div]:!gap-0.5 [&>div>div]:!flex-none">
            <RichTextEditorFormattingToolbar
              showEmojiPicker
              hideRichTextHeaderOptions
              hideImageUpload
              hideListOptions
              hideSeparators
              buttonStyle={"ghost"}
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={activeList ? "secondary" : "ghost"}
                size="icon-md"
                className="gap-0.5 px-1.5"
                aria-label="List"
              >
                {activeList?.icon ?? <UnorderedListIcon />}
                <ChevronDownIcon className="text-gray-8" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              {listOptions.map((option) => (
                <DropdownMenuItem
                  key={option.key}
                  onClick={option.onClick}
                  className={cn(option.active && "bg-gray-1")}
                >
                  <span className="flex size-4 items-center justify-center text-gray-9">
                    {option.icon}
                  </span>
                  <Text type="P2" className="text-gray-12">
                    {option.label}
                  </Text>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <BuilderBottomToolbarSeparator />

          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <AddMediaDialog
                    onMediaChange={(media) => onMediaChange(media)}
                    onResourceChange={(trainingResource) => {
                      const newMedia = trainingResource.publishedVersion?.media;
                      if (!newMedia) return;
                      onMediaChange(newMedia);
                    }}
                    title="Add media"
                    filterResources={[LinkType.IMAGE, LinkType.GIF]}
                    uploadAccept=".gif,.png,.jpg,.jpeg,.webp"
                    trigger={
                      <Button variant="ghost" size="icon-md">
                        <PhotoOutlineIcon />
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Media</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={
                    tiptapEditorContext?.formatting.isTable
                      ? "secondary"
                      : "ghost"
                  }
                  size="icon-md"
                  onClick={() => {
                    if (tiptapEditorContext?.formatting.isTable) {
                      return;
                    }
                    const startPos =
                      tiptapEditorContext?.editor?.state.selection.$anchor
                        .pos || 0;
                    tiptapEditorContext?.editor
                      ?.chain()
                      .focus()
                      .insertContent(
                        "<p></p><table><tr><td><p></p></td><td><p></p></td></tr><tr><td><p></p></td><td><p></p></td></tr></table><p></p>",
                      )
                      .setTextSelection(startPos + 4)
                      .run();
                  }}
                >
                  <Table3RowsOutlineIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Table</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-md"
                  onClick={() => {
                    tiptapEditorContext?.editor
                      ?.chain()
                      .focus()
                      .setHorizontalRule()
                      .run();
                  }}
                >
                  <LineDashedIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Separator</TooltipContent>
            </Tooltip>
          </div>

          <BuilderBottomToolbarSeparator />
        </>
      )}

      <div className="flex items-center gap-0.5">
        {viewModeButtons.map((option) => {
          const active = currentViewMode === option.key;
          return (
            <Tooltip key={option.key}>
              <TooltipTrigger asChild>
                <Button
                  variant={active ? "secondary" : "ghost"}
                  size="icon-md"
                  onClick={() => setTrainingResourceViewMode(option.key)}
                >
                  {option.icon}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{option.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </BuilderBottomToolbar>
  );
};

export default TrainingResourceRichTextEditorBottomToolbar;
